import {
  CustomerPackageOffer,
  LinearViewingMethod,
  Maybe,
  ViewingMethod,
  VodViewingMethod,
} from '@/lib/graph-ql';
import {
  findOfferInPackages,
  getBasePrice,
  getDiscountMonth,
  getDiscountPrice,
  getHintMsg,
  getPromoOffer,
  isContentValidForVodOrLinear,
} from '@/Utils/Puj';
import { useMemo } from 'react';

type ViewingMethods = VodViewingMethod & LinearViewingMethod;

export interface PackageDetails {
  hintMessage: string | null;
  isPackageDetailPage: boolean;
  packagePrice?: number;
  packageTitle?: string;
  offerText?: string;
  offerId?: string;
  collectionId?: string;
  collectionTitle?: string;
  discountPrice?: number | null;
  actualPrice?: number | string;
}

const usePackageDetails = (
  offerData: CustomerPackageOffer,
  viewingMethods: Maybe<ViewingMethod>[],
  isPackageDetailPage: boolean,
) => {
  // Extract the offer from offerData or from viewingMethods
  const offer = useMemo(() => {
    return isPackageDetailPage
      ? offerData
      : viewingMethods &&
          viewingMethods
            .map(viewingMethod => viewingMethod as ViewingMethods)
            .find(data => isContentValidForVodOrLinear(data) && findOfferInPackages(data?.packages))
            ?.packages?.find(pkg => pkg?.offer)?.offer;
  }, [offerData, viewingMethods, isPackageDetailPage]);

  // Memoized values
  const discountPrice = useMemo(
    () => getPromoOffer(offer?.price?.discount, offer?.price?.billingFrequency),
    [offer],
  );
  const offerText = useMemo(() => {
    const discount = getDiscountPrice(
      offer?.price?.basePrice?.amount,
      offer?.price?.discount?.value,
      offer?.price?.billingFrequency,
    );
    return `${offer?.title} for ${
      discount ?? getBasePrice(offer?.price?.basePrice?.amount, offer?.price?.billingFrequency)
    }`;
  }, [offer]);
  const actualPrice = useMemo(
    () =>
      getDiscountPrice(
        offer?.price?.basePrice?.amount,
        offer?.price?.discount?.value,
        offer?.price?.billingFrequency,
      ) ?? getBasePrice(offer?.price?.basePrice?.amount, offer?.price?.billingFrequency, false),
    [offer],
  );
  const hintMessage = useMemo(
    () =>
      getHintMsg(
        getDiscountPrice(
          offer?.price?.basePrice?.amount,
          offer?.price?.discount?.value,
          offer?.price?.billingFrequency,
        ),
        getDiscountMonth(offer?.price?.discount?.duration),
        offer?.price?.basePrice?.amount,
        offer?.price?.billingFrequency,
        offer?.title,
        isPackageDetailPage,
      ),
    [offer, isPackageDetailPage],
  );

  const basePrice = useMemo(() => offer?.price?.basePrice?.amount, [offer]);
  const offerId = useMemo(() => offer?.id, [offer]);
  const collectionId = useMemo(() => offer?.collectionLink?.collection.id, [offer]);
  const collectionTitle = useMemo(() => offer?.collectionLink?.collection.title, [offer]);
  const packageTitle = useMemo(() => offer?.title, [offer]);
  const heroImageUrl = useMemo(() => offer?.heroImage?.uri, [offer]);
  const logoImages = useMemo(() => offer?.logoImages?.map(item => item?.uri) ?? [], [offer]);

  return {
    discountPrice,
    offerText,
    packageTitle,
    actualPrice,
    hintMessage,
    basePrice,
    heroImageUrl,
    collectionId,
    collectionTitle,
    logoImages,
    offerId,
  };
};

export default usePackageDetails;
